import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Button,
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import StoreIcon from '@mui/icons-material/Store';
import EventIcon from '@mui/icons-material/Event';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import apiService from '../services/api';
import liffService from '../services/liff';
import { useAuth } from '../auth/AuthContext';
import { LaborConditionsAgreementSummary } from '../types';

const formatDate = (isoDate?: string | null): string => {
  if (!isoDate) return '---';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '---';
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`;
};

const LaborConditionsPage: React.FC = () => {
  const navigate = useNavigate();
  const { userId } = useAuth();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [agreements, setAgreements] = useState<LaborConditionsAgreementSummary[]>([]);
  const [lineUserId, setLineUserId] = useState<string | null>(null);

  const fetchAgreements = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);
      // バックエンドは StoreUser.line_user_id で照合するため、アプリ側UUIDではなくLINE UIDを渡す
      const profile = await liffService.getProfile();
      if (!profile?.userId) {
        setError('ユーザー情報の取得に失敗しました');
        return;
      }
      setLineUserId(profile.userId);
      const res = await apiService.getLaborConditionsAgreements(profile.userId);
      if (res.success && res.data) {
        setAgreements(res.data);
      } else {
        setError(res.error || '労働条件通知書の取得に失敗しました');
      }
    } catch (err) {
      setError('労働条件通知書の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchAgreements();
  }, [fetchAgreements]);

  const handleDownload = (agreementId: string) => {
    if (!lineUserId) return;
    const url = apiService.getLaborConditionsDownloadUrl(agreementId, lineUserId);
    if (liffService.isInClient()) {
      // LINE内ブラウザではPDFの保存ができないため外部ブラウザで開く
      liffService.openWindow(url, true);
    } else {
      window.open(url, '_blank');
    }
  };

  return (
    <Box sx={{ pt: 0, px: 0, pb: 2, m: 0 }}>
      {/* ページヘッダー */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        px: 1,
        pt: 1.5,
        pb: 0.5,
        minHeight: 48,
      }}>
        <IconButton onClick={() => navigate('/mypage')} size="small" sx={{ color: '#3c3c3c' }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Typography sx={{
          flex: 1,
          fontWeight: 700,
          fontSize: '16px',
          color: '#3c3c3c',
          pl: 0.5,
        }}>
          労働条件通知書
        </Typography>
      </Box>

      <Box sx={{ px: 2, pt: 1 }}>
        <Typography variant="caption" sx={{ color: '#888' }}>
          同意した労働条件通知書をダウンロードできます
        </Typography>
      </Box>

      <Box sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography sx={{ textAlign: 'center', color: 'error.main', my: 4 }}>
            {error}
          </Typography>
        ) : agreements.length === 0 ? (
          <Typography sx={{ textAlign: 'center', color: 'text.secondary', my: 4 }}>
            同意済みの労働条件通知書はありません
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {agreements.map((agreement) => (
              <Card key={agreement.agreement_id} sx={{ border: '1px solid #e0e0e0' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  {/* 店舗名 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <StoreIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '15px' }}>
                      {agreement.store_name || '---'}
                    </Typography>
                  </Box>

                  {/* 職種 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <WorkOutlineIcon sx={{ mr: 1, color: '#898989', fontSize: 18 }} />
                    <Typography sx={{ fontSize: '13px', color: '#3c3c3c' }}>
                      {agreement.job_title || '---'}
                    </Typography>
                  </Box>

                  {/* 就労日・同意日 */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EventIcon sx={{ mr: 1, color: '#898989', fontSize: 18 }} />
                    <Typography sx={{ fontSize: '13px', color: '#3c3c3c' }}>
                      就労日: {formatDate(agreement.shift_date)}（同意日: {formatDate(agreement.agreed_at)}）
                    </Typography>
                  </Box>

                  {/* ダウンロードボタン */}
                  <Button
                    variant="outlined"
                    size="small"
                    fullWidth
                    startIcon={<FileDownloadIcon />}
                    onClick={() => handleDownload(agreement.agreement_id)}
                    sx={{
                      borderRadius: 5,
                      border: 'none',
                      bgcolor: '#cce3f9',
                      color: '#0071e3',
                      fontWeight: 700,
                      fontSize: '13px',
                      '&:hover': { border: 'none', bgcolor: '#b3d4f5' },
                    }}
                  >
                    PDFをダウンロード
                  </Button>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default LaborConditionsPage;
